import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";
import SocialShare from "@/components/SocialShare";

const STORAGE_KEY = (slug) => `rb_engaged_${slug}`;
const OWNED_KEY = "rb_owned_memorials";

function isOwnedMemorial(slug) {
  try {
    const raw = localStorage.getItem(OWNED_KEY);
    if (!raw) return false;
    const arr = JSON.parse(raw);
    return Array.isArray(arr) && arr.includes(slug);
  } catch { return false; }
}

export default function MemorialEngagement({ slug, petName, locked = false, upgradeHref }) {
  const [count, setCount] = useState(0);
  const [tributes, setTributes] = useState([]);
  const [name, setName] = useState("");
  const [msg, setMsg] = useState("");
  const [lighting, setLighting] = useState(false);
  const [sending, setSending] = useState(false);
  const [hasLit, setHasLit] = useState(false);
  const [justLit, setJustLit] = useState(false);
  const [ownedDismissed, setOwnedDismissed] = useState(false);
  const owned = isOwnedMemorial(slug);

  useEffect(() => {
    if (locked) return; // no engagement fetch on demos
    const lit = localStorage.getItem(STORAGE_KEY(slug)) === "1";
    setHasLit(lit);
    apiClient.get(`/memorials/${slug}/engagement`).then(({ data }) => {
      setCount(data.candle_count || 0);
      setTributes(data.tributes || []);
    }).catch(() => {});
  }, [slug, locked]);

  if (locked) {
    return (
      <section className="mt-16" data-testid="engagement-locked">
        <div className="max-w-2xl mx-auto glass p-8 md:p-10 rounded-sm text-center border border-[var(--rb-gold)]/30">
          <div className="tracking-celestial text-[var(--rb-gold)] mb-3">Candle · Tributes · Sharing</div>
          <h3 className="font-serif-display text-2xl md:text-3xl text-[var(--rb-text-heading)] leading-snug">
            Let loved ones gather around <span className="italic">{petName}</span>.
          </h3>
          <p className="font-serif-display italic text-[16px] text-[var(--rb-text-body)] leading-relaxed mt-4 max-w-lg mx-auto">
            Candle lighting, tributes and the shareable page are unlocked when this memorial becomes permanent.
            <br/>One-time $29 keeps it here forever ✦
          </p>
          {upgradeHref && (
            <div className="mt-6">
              <Link to={upgradeHref} className="btn-gold" data-testid="engagement-locked-upgrade-cta">
                Keep this memorial forever — $29
              </Link>
            </div>
          )}
        </div>
      </section>
    );
  }

  const lightCandle = async () => {
    if (hasLit) {
      toast("A candle is already burning from you ✦");
      return;
    }
    setLighting(true);
    try {
      const { data } = await apiClient.post(`/memorials/${slug}/candles`);
      setCount(data.candle_count);
      setHasLit(true);
      setJustLit(true);
      localStorage.setItem(STORAGE_KEY(slug), "1");
      toast.success(`A candle now burns for ${petName} ✦`);
    } catch {
      toast.error("Could not light the candle.");
    } finally {
      setLighting(false);
    }
  };

  const submitTribute = async (e) => {
    e.preventDefault();
    if (!msg.trim()) {
      toast.error("Please share a few words.");
      return;
    }
    if (msg.length > 500) {
      toast.error("Please keep your message under 500 characters.");
      return;
    }
    setSending(true);
    try {
      const { data } = await apiClient.post(`/memorials/${slug}/tributes`, {
        visitor_name: name.trim() || null,
        message: msg.trim(),
      });
      setTributes((p) => [data, ...p]);
      setMsg("");
      setName("");
      toast.success("Your tribute is placed beneath the meadow ✦");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not post your tribute.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="mt-16 space-y-12">
      {/* Candle */}
      <div className="text-center">
        <div className="relative inline-block">
          <Flame burning={count > 0 || lighting} />
        </div>
        <div className="font-serif-display italic text-[22px] text-[var(--rb-text-heading)] mt-4">
          {count === 0 ? (
            <>No candles yet — be the first to light one for <span className="italic">{petName}</span>.</>
          ) : (
            <>{count} {count === 1 ? "candle is burning" : "candles are burning"} for {petName}.</>
          )}
        </div>
        <div className="mt-5">
          <button onClick={lightCandle} disabled={lighting || hasLit} className="btn-gold" data-testid="memorial-light-candle-btn">
            {hasLit ? "Candle lit ✦" : lighting ? "Lighting…" : "Light a candle"}
          </button>
        </div>

        {/* Gentle share prompt — appears once a candle has been lit */}
        {(hasLit || justLit) && (
          <div className="mt-8 max-w-md mx-auto" data-testid="post-candle-share">
            <p className="font-serif-display italic text-[15px] text-[var(--rb-text-muted)] mb-4">
              Invite their family and friends to remember {petName} too.
            </p>
            <SocialShare slug={slug} petName={petName} />
          </div>
        )}
      </div>

      {/* TENDER UPSELL — only shows to the person who created this memorial, only after they light a candle */}
      {owned && justLit && !ownedDismissed && (
        <div className="max-w-2xl mx-auto" data-testid="owned-memorial-upsell">
          <div className="relative overflow-hidden rounded-sm border border-[var(--rb-gold)]/40 bg-gradient-to-br from-white via-[#FDFBF7] to-[#FAF6EC] shadow-[0_30px_80px_-20px_rgba(212,175,55,0.3)] p-8 md:p-10 text-center">
            <button
              onClick={() => setOwnedDismissed(true)}
              className="absolute top-3 right-4 text-[var(--rb-text-muted)] hover:text-[var(--rb-text-heading)] text-xl leading-none"
              aria-label="Close"
              data-testid="owned-memorial-upsell-dismiss"
            >×</button>
            <div className="tracking-celestial text-[var(--rb-gold)] mb-3">A quiet thought ✦</div>
            <h3 className="font-serif-display text-2xl md:text-3xl text-[var(--rb-text-heading)] leading-snug">
              Would you like to bring <span className="italic">{petName}&apos;s scene</span><br/>home as a real diorama?
            </h3>
            <p className="font-serif-display italic text-[16px] text-[var(--rb-text-body)] leading-relaxed mt-4 max-w-lg mx-auto">
              An heirloom you can hold — sculpted by hand, sealed beneath glimmering glass,
              and inscribed with the verse written just for them.
            </p>
            <div className="inline-flex items-center gap-3 px-4 py-1.5 mt-5 mb-5 rounded-full border border-[var(--rb-gold)]/40 bg-white/70">
              <span className="text-[10px] tracking-[0.2em] uppercase text-[var(--rb-text-muted)]">From</span>
              <span className="font-serif-display text-xl text-[var(--rb-gold)]">$150</span>
              <span className="text-[10px] tracking-[0.2em] uppercase text-[var(--rb-text-muted)]">· $50 holds your spot</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link
                to={`/commission?memorial=${slug}`}
                onClick={() => apiClient.post(`/memorials/${slug}/track`, { event: "commission_click" }).catch(() => {})}
                className="btn-gold"
                data-testid="owned-memorial-upsell-cta"
              >
                Commission their diorama
              </Link>
              <button onClick={() => setOwnedDismissed(true)} className="text-[11px] tracking-[0.25em] uppercase text-[var(--rb-text-muted)] hover:text-[var(--rb-text-heading)] transition-colors">
                Maybe later
              </button>
            </div>
            <p className="text-[10px] tracking-[0.2em] uppercase text-[var(--rb-text-muted)] mt-5 italic">No obligation · reply within 1–2 days</p>
          </div>
        </div>
      )}

      {/* Tribute form */}
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <div className="tracking-celestial text-[var(--rb-gold)] mb-2">Leave a remembrance</div>
          <p className="font-serif-display italic text-[var(--rb-text-muted)] text-[17px]">
            Share a memory, a kind word, or a quiet thought for {petName}.
          </p>
        </div>
        <form onSubmit={submitTribute} className="glass p-6 md:p-8 rounded-sm space-y-4">
          <div>
            <label className="rb-label">Your name (optional)</label>
            <input className="rb-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Anonymous" maxLength={60} data-testid="tribute-name-input"/>
          </div>
          <div>
            <label className="rb-label">Your message · {500 - msg.length} characters left</label>
            <textarea className="rb-textarea" value={msg} onChange={(e) => setMsg(e.target.value)} rows={4} maxLength={500} placeholder={`Words for ${petName}...`} data-testid="tribute-message-input"/>
          </div>
          <button type="submit" disabled={sending} className="btn-gold w-full justify-center" data-testid="tribute-submit-btn">
            {sending ? "Placing your tribute…" : "Place tribute"}
          </button>
        </form>
      </div>

      {/* Tributes list */}
      {tributes.length > 0 && (
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8 ornament"><span>✦</span></div>
          <div className="space-y-5">
            {tributes.map((t) => (
              <div key={t.id} className="memorial-card p-6 md:p-8" data-testid={`tribute-${t.id}`}>
                <p className="font-serif-display italic text-[18px] text-[var(--rb-text-heading)] leading-relaxed whitespace-pre-wrap">{t.message}</p>
                <div className="mt-4 flex items-center justify-between text-[11px] tracking-[0.22em] uppercase">
                  <span className="text-[var(--rb-gold)]">{t.visitor_name || "Anonymous"}</span>
                  <span className="text-[var(--rb-text-muted)]">{new Date(t.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function Flame({ burning }) {
  return (
    <div className="flex flex-col items-center" aria-hidden>
      <div className={`relative ${burning ? "flame-on" : "flame-off"}`}>
        <svg width="56" height="80" viewBox="0 0 56 80">
          {/* Flame */}
          <ellipse cx="28" cy="24" rx="9" ry="18" fill="url(#flame-grad)" className="flame-shape" />
          <circle cx="28" cy="32" r="6" fill="#fff7d6" opacity="0.85" className="flame-core" />
          <defs>
            <radialGradient id="flame-grad" cx="0.5" cy="0.7" r="0.6">
              <stop offset="0%" stopColor="#FFF5C0" />
              <stop offset="50%" stopColor="#F8CD46" />
              <stop offset="100%" stopColor="#E27A2A" stopOpacity="0.85" />
            </radialGradient>
          </defs>
          {/* Wick */}
          <rect x="27" y="42" width="2" height="6" fill="#2C2825" />
          {/* Candle body */}
          <rect x="18" y="48" width="20" height="24" rx="3" fill="#FDFBF7" stroke="#E8DFC8"/>
          <rect x="18" y="48" width="20" height="4" rx="2" fill="#F2E3C6"/>
        </svg>
      </div>
    </div>
  );
}
